// components.jsx — building blocks for the executive summary (ported from the design, V2).
import React, { useState } from "react";
import {
  TYPE_COLOR, esScoreColor, SWITCHBOARD_GROUPS, ASSESSMENT_BY_ID,
  scoreBands, scoreHigherWorse, bandAt, TYPE_LEGEND,
} from "./data";
import OrgFilter from "../components/OrgFilter";
import PickFilter from "../components/PickFilter";

// Vasana disposition types (stored assessment result codes) for the type filter,
// each with its system color for the pill chips.
const VASANA_TYPE_OPTIONS = [
  { id: "Sa", name: "Sa", color: TYPE_COLOR.SA },
  { id: "Re", name: "Re", color: TYPE_COLOR.RE },
  { id: "Ga", name: "Ga", color: TYPE_COLOR.GA },
  { id: "Ma", name: "Ma", color: TYPE_COLOR.MA },
];

// Age brackets rendered as selectable chips; each maps to an age_min/age_max range.
const AGE_BRACKETS = [
  { id: "18-29", label: "18–29", min: "18", max: "29" },
  { id: "30-39", label: "30–39", min: "30", max: "39" },
  { id: "40-49", label: "40–49", min: "40", max: "49" },
  { id: "50-59", label: "50–59", min: "50", max: "59" },
  { id: "60+", label: "60+", min: "60", max: "" },
];

// ---- Info tooltip (hover on desktop, tap on mobile) ----
export function InfoTip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="es-infotip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}>
      <button type="button" className="es-infotip__btn" aria-label="More information" aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        <i className="ph ph-info"></i>
      </button>
      <span className={"es-infotip__card" + (open ? " is-open" : "")} role="tooltip">{text}</span>
    </span>
  );
}

// ---- Trend pill ----
export function TrendPill({ trend }) {
  if (!trend) return null;
  const { dir, value, improving } = trend;
  const cls = improving === true ? "es-trend--up" : improving === false ? "es-trend--down" : "es-trend--flat";
  const icon = dir === "up" ? "ph-arrow-up-right" : dir === "down" ? "ph-arrow-down-right" : "ph-minus";
  const label = dir === "flat" ? "baseline" : value + " vs prior";
  return (
    <span className={"es-trend " + cls}>
      <i className={"ph " + icon}></i>
      {label}
    </span>
  );
}

// ---- Toggle (switchboard control) ----
export function Toggle({ a, on, onClick }) {
  const dot = a.categorical ? "var(--vas-primary-tint)" : esScoreColor(a);
  return (
    <button
      className={"es-toggle " + (on ? "is-on" : "is-off")}
      onClick={onClick}
      aria-pressed={on}
      title={(on ? "On" : "Off") + " — " + a.name}
    >
      <span className="es-toggle__sw"></span>
      <span className="es-toggle__body">
        <span className="es-toggle__name">{a.name}</span>
        {a.abbr ? <span className="es-toggle__abbr">{a.abbr}</span> : null}
      </span>
      <span className="es-toggle__dot" style={{ background: on ? dot : "rgba(255,255,255,0.15)" }}></span>
    </button>
  );
}

// ---- Score card ----
const VASANA_ORDER = ["SA", "RE", "GA", "MA"];
function sortedMix(mix) {
  return [...mix].sort((a, b) => VASANA_ORDER.indexOf(a.type) - VASANA_ORDER.indexOf(b.type));
}
function dominantType(mix) {
  const max = Math.max(...mix.map((m) => m.pct));
  const top = mix.filter((m) => m.pct === max);
  return top.length === 1 ? top[0].type : null; // null when tied
}

export function ScoreCard({ a, onOpen }) {
  const hasData = a.categorical ? a.mix && a.mix.length > 0 : a.responses > 0 && a.score != null;
  const accent = a.categorical ? "var(--vas-primary)" : esScoreColor(a);
  const dom = a.categorical && a.mix ? dominantType(a.mix) : null;
  return (
    <button className="es-score" onClick={() => onOpen(a)}>
      <div className="es-score__top">
        <div>
          <div className="es-score__name">{a.name}</div>
          {a.abbr ? <div className="es-score__abbr">{a.abbr}</div> : null}
        </div>
        <span className="es-score__accent" style={{ background: hasData ? accent : "rgba(255,255,255,0.15)" }}></span>
      </div>

      {!hasData ? (
        <div className="es-score__score" style={{ fontSize: 18, color: "var(--vas-on-dark-3)" }}>No data</div>
      ) : a.categorical ? (
        <div className="es-mix">
          {sortedMix(a.mix).map((m) => (
            <div className="es-mix__row" key={m.type}>
              <span className="es-mix__lbl" style={{ color: TYPE_COLOR[m.type] }}>
                {m.type[0] + m.type[1].toLowerCase()}
              </span>
              <span className="es-mix__track">
                <span className="es-mix__fill" style={{ width: m.pct + "%", background: TYPE_COLOR[m.type] }}></span>
              </span>
              <span className="es-mix__pct">{m.pct}%</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="es-score__score">
          <span style={{ color: accent }}>{a.score}</span>
          <small>/100</small>
        </div>
      )}

      <div className="es-score__foot">
        {!hasData ? (
          <span className="es-chip" style={{ background: "rgba(255,255,255,0.05)", color: "var(--vas-on-dark-3)" }}>
            No responses
          </span>
        ) : a.categorical ? (
          dom && (
            <span className="es-chip es-chip--type" style={{ color: TYPE_COLOR[dom], borderColor: TYPE_COLOR[dom] }}>
              <span className="es-chip__dot" style={{ background: TYPE_COLOR[dom] }}></span>
              Dominant {dom[0] + dom[1].toLowerCase()}
            </span>
          )
        ) : (
          <span
            className="es-chip"
            style={{ color: accent, borderColor: accent, background: `color-mix(in srgb, ${accent} 15%, transparent)` }}
          >
            {a.band}
          </span>
        )}
      </div>
    </button>
  );
}

// ---- Score legend (shows what each band on the scale means) ----
export function ScoreLegend({ a }) {
  if (a.categorical) {
    return (
      <div className="es-drawer__section">
        <h5>Type legend</h5>
        <div className="es-legkey">
          {TYPE_LEGEND.map((t) => (
            <div className="es-legkey__row" key={t.type}>
              <span className="es-legkey__dot" style={{ background: TYPE_COLOR[t.type] }}></span>
              <span className="es-legkey__lbl">{t.label}</span>
            </div>
          ))}
        </div>
        <p className="es-legkey__note">A categorical disposition profile — how the group moves under pressure. Not a 0–100 health score, so it is excluded from the index.</p>
      </div>
    );
  }
  const bands = scoreBands(a);
  const v = a.score;
  if (v == null || !bands) return null;
  const higherWorse = scoreHigherWorse(a);
  const activeIdx = bands.indexOf(bandAt(bands, v));
  return (
    <div className="es-drawer__section">
      <h5>Score legend</h5>
      <div className="es-legkey__bar">
        {bands.map((b) => (
          <span className="es-legkey__seg" key={b.label} style={{ flexGrow: b.max - b.min + 1, background: b.color }}></span>
        ))}
        <span className="es-legkey__marker" style={{ left: v + "%" }} aria-hidden="true">
          <span className="es-legkey__markerval">{v}</span>
        </span>
      </div>
      <div className="es-legkey">
        {bands.map((b, i) => (
          <div className={"es-legkey__row" + (i === activeIdx ? " is-active" : "")} key={b.label}>
            <span className="es-legkey__dot" style={{ background: b.color }}></span>
            <span className="es-legkey__lbl">{b.label}</span>
            <span className="es-legkey__rng">{b.min}–{b.max}</span>
          </div>
        ))}
      </div>
      <p className="es-legkey__note">
        Bands are specific to this assessment.{" "}
        {higherWorse
          ? "Here a higher score means more of the condition — raw score is inverted for the readiness index."
          : "Scores are health-aligned, so a higher score is healthier."}
      </p>
    </div>
  );
}

// ---- Detail drawer ----
export function Drawer({ a, population, onClose }) {
  const open = !!a;
  return (
    <>
      <div className={"es-drawer-scrim" + (open ? " is-open" : "")} onClick={onClose}></div>
      <aside className={"es-drawer" + (open ? " is-open" : "")} aria-hidden={!open}>
        {a && (
          <>
            <div className="es-drawer__head">
              <div>
                <span className="es-eyebrow">{a.domain}</span>
                <h3 className="es-drawer__name">{a.name}</h3>
                {a.abbr ? <div className="es-drawer__abbr">{a.abbr}</div> : null}
              </div>
              <button className="es-drawer__close" onClick={onClose} aria-label="Close">
                <i className="ph ph-x"></i>
              </button>
            </div>
            <div className="es-drawer__body">
              {a.categorical ? (
                <div>
                  <div className="es-mix">
                    {sortedMix(a.mix).map((m) => (
                      <div className="es-mix__row" key={m.type} style={{ gridTemplateColumns: "36px 1fr 44px", marginBottom: 4 }}>
                        <span className="es-mix__lbl" style={{ color: TYPE_COLOR[m.type], fontSize: 20 }}>
                          {m.type[0] + m.type[1].toLowerCase()}
                        </span>
                        <span className="es-mix__track" style={{ height: 8 }}>
                          <span className="es-mix__fill" style={{ width: m.pct + "%", background: TYPE_COLOR[m.type] }}></span>
                        </span>
                        <span className="es-mix__pct" style={{ fontSize: 13 }}>{m.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : a.responses > 0 && a.score != null ? (
                <div className="es-drawer__score">
                  <div className="es-drawer__bignum" style={{ color: esScoreColor(a) }}>
                    {a.score}
                    <small>/100</small>
                  </div>
                  <div className="es-drawer__stack">
                    <span
                      className="es-chip"
                      style={{ color: esScoreColor(a), borderColor: esScoreColor(a), background: `color-mix(in srgb, ${esScoreColor(a)} 15%, transparent)` }}
                    >
                      {a.band}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="es-drawer__score">
                  <div className="es-drawer__bignum" style={{ color: "var(--vas-on-dark-3)", fontSize: 40 }}>No data</div>
                </div>
              )}

              <div className="es-drawer__section">
                <h5>What this signals</h5>
                <p>{a.desc}</p>
              </div>
              <div className="es-drawer__section">
                <h5>What it measures</h5>
                <p>{a.measures}</p>
              </div>

              <ScoreLegend a={a} />

              <div className="es-drawer__rows">
                <div className="es-drawer__row">
                  <span>Population in scope</span>
                  <b>{population != null ? population.toLocaleString("en-US") : "—"}</b>
                </div>
                {!a.categorical && (
                  <div className="es-drawer__row">
                    <span>Counts toward index</span>
                    <b>{a.invert ? "Yes (inverted)" : "Yes"}</b>
                  </div>
                )}
                {a.categorical && (
                  <div className="es-drawer__row">
                    <span>Counts toward index</span>
                    <b>No</b>
                  </div>
                )}
              </div>
              <p className="es-drawer__placeholder">Breakdown by station and shift available in the full assessment view.</p>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

// ---- Config panel: organization filter + grouped assessment switchboard ----
// V2's slide-out. Scoped to what the backend supports: the live org filter + the
// assessment on/off toggles (no date-range / demographic filters).
function CfgToggleRow({ a, on, onClick }) {
  const dot = a.categorical ? "var(--vas-primary-tint)" : esScoreColor(a);
  return (
    <button className={"cfg-row" + (on ? " is-on" : "")} onClick={onClick} aria-pressed={on}>
      <span className="cfg-row__dot" style={{ background: on ? dot : "rgba(255,255,255,0.16)" }}></span>
      <span className="cfg-row__name">{a.name}</span>
      {a.abbr ? <span className="cfg-row__abbr">{a.abbr}</span> : null}
      <span className="cfg-row__sw"></span>
    </button>
  );
}

function CfgZone({ title, count, open, onToggle, children }) {
  return (
    <section className={"cfg-zone" + (open ? " is-open" : " is-collapsed")}>
      <button className="cfg-zone__bar" onClick={onToggle} aria-expanded={open}>
        <h3 className="cfg-zone__title">{title}</h3>
        {count != null && <span className="cfg-zone__badge">{count}</span>}
        <i className="ph ph-caret-down cfg-zone__chev"></i>
      </button>
      <div className="cfg-zone__panel"><div className="cfg-zone__inner">{children}</div></div>
    </section>
  );
}

export function ConfigPanel({
  open, onClose, active, toggle, selectAll, clearAll,
  organizations, filters, options, onChange, lockOrg = false, orgName,
}) {
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [switchOpen, setSwitchOpen] = useState(true);

  // An org admin is locked to their own organization: the picker is hidden and the
  // scope always counts as "has org", so the dependent filters stay enabled.
  const hasOrg = lockOrg || !!filters.org_id;
  // Cascade the option tree client-side: departments belong to the chosen location,
  // events to the chosen department. With no parent chosen, offer all in-scope.
  const departments = filters.location_id
    ? options.departments.filter((d) => String(d.location_id) === String(filters.location_id))
    : options.departments;
  const events = filters.department_id
    ? options.events.filter((e) => String(e.department_id) === String(filters.department_id))
    : options.events;
  const set = (key) => (v) => onChange({ [key]: v });

  const total = SWITCHBOARD_GROUPS.reduce((s, g) => s + g.ids.length, 0);
  const activeCount = SWITCHBOARD_GROUPS.reduce(
    (s, g) => s + g.ids.filter((id) => active[id]).length,
    0
  );

  return (
    <>
      <div className={"cfg-scrim" + (open ? " is-open" : "")} onClick={onClose}></div>
      <aside className={"cfg-panel" + (open ? " is-open" : "")} aria-hidden={!open} aria-label="Filters">
        <div className="cfg-head">
          <div>
            <span className="cfg-head__eyebrow"><i className="ph ph-sliders-horizontal"></i>Filters</span>
            <h2 className="cfg-head__title">Filters &amp; assessments</h2>
          </div>
          <button className="cfg-close" onClick={onClose} aria-label="Close panel"><i className="ph ph-x"></i></button>
        </div>

        <div className="cfg-body">
          {/* FILTERS ZONE — scope every number on the dashboard. */}
          <CfgZone title="Filters" open={filtersOpen} onToggle={() => setFiltersOpen((o) => !o)}>
            <p className="cfg-zone__sub">Adjust the filters to change what the dashboard shows.</p>

            {lockOrg ? (
              <div className="cfg-field">
                <span className="cfg-field__label">Organization</span>
                <div
                  className="cfg-locked"
                  aria-label={"Scoped to " + (orgName || "your organization")}
                >
                  <i className="ph-fill ph-buildings"></i>
                  <span className="cfg-locked__name">{orgName || "Your organization"}</span>
                  <i className="ph ph-lock-simple cfg-locked__lock"></i>
                </div>
              </div>
            ) : (
              <div className="cfg-field">
                <span className="cfg-field__label">Organization</span>
                <OrgFilter organizations={organizations} value={filters.org_id} onChange={set("org_id")} />
              </div>
            )}

            <div className="cfg-field">
              <span className="cfg-field__label">Date range</span>
              <div className="cfg-daterange">
                <div className="cfg-daterange__col">
                  <span className="cfg-daterange__sub">From</span>
                  <input
                    type="date" className="cfg-input"
                    value={filters.date_from}
                    max={filters.date_to || undefined}
                    onChange={(e) => onChange({ date_from: e.target.value })}
                  />
                </div>
                <i className="ph ph-arrow-right cfg-daterange__arrow"></i>
                <div className="cfg-daterange__col">
                  <span className="cfg-daterange__sub">To</span>
                  <input
                    type="date" className="cfg-input"
                    value={filters.date_to}
                    min={filters.date_from || undefined}
                    onChange={(e) => onChange({ date_to: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="cfg-field">
              <span className="cfg-field__label">Location</span>
              <PickFilter
                options={options.locations}
                value={filters.location_id}
                onChange={set("location_id")}
                icon="ph-map-pin"
                allLabel="All stations"
                placeholder="Search locations…"
                disabled={!hasOrg}
                disabledLabel="Select an organization first"
              />
            </div>

            <div className="cfg-field">
              <span className="cfg-field__label">Department</span>
              <PickFilter
                options={departments}
                value={filters.department_id}
                onChange={set("department_id")}
                icon="ph-users-three"
                allLabel="All departments"
                placeholder="Search departments…"
                disabled={!hasOrg}
                disabledLabel="Select an organization first"
              />
            </div>

            <div className="cfg-field">
              <span className="cfg-field__label">Event</span>
              <PickFilter
                options={events}
                value={filters.event_id}
                onChange={set("event_id")}
                icon="ph-calendar-blank"
                allLabel="All events"
                placeholder="Search events…"
                disabled={!hasOrg}
                disabledLabel="Select an organization first"
              />
            </div>

            <div className="cfg-field">
              <span className="cfg-field__label">Vasana type</span>
              <div className="cfg-chips">
                {VASANA_TYPE_OPTIONS.map((t) => {
                  const on = String(filters.vasana_type) === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={"cfg-chip" + (on ? " is-on" : "")}
                      style={{ borderColor: t.color, ...(on ? { background: t.color, color: "#fff" } : null) }}
                      onClick={() => onChange({ vasana_type: on ? "" : t.id })}
                    >
                      <span className="cfg-chip__dot" style={{ background: on ? "#fff" : t.color }}></span>
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="cfg-field">
              <span className="cfg-field__label">Age distribution</span>
              <div className="cfg-chips">
                {AGE_BRACKETS.map((b) => {
                  const on = String(filters.age_min) === b.min && String(filters.age_max) === b.max;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      className={"cfg-chip" + (on ? " is-on" : "")}
                      onClick={() => onChange(on ? { age_min: "", age_max: "" } : { age_min: b.min, age_max: b.max })}
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </CfgZone>

          <div className="cfg-divider"></div>

          {/* SWITCHBOARD ZONE */}
          <CfgZone title="Assessment switchboard" count={activeCount + " / " + total} open={switchOpen} onToggle={() => setSwitchOpen((o) => !o)}>
            <p className="cfg-zone__sub">Customize your view. Toggle assessments on or off to recompose the index and scorecard in real time.</p>
            <div className="cfg-zone__meta">
              <span className="cfg-count"><b>{activeCount}</b> of {total} active</span>
              <div className="cfg-bulk">
                <button className="cfg-link" onClick={selectAll}>Select all</button>
                <span className="cfg-bulk__div"></span>
                <button className="cfg-link" onClick={clearAll}>Clear all</button>
              </div>
            </div>

            <div className="cfg-groups">
              {SWITCHBOARD_GROUPS.map((g) => (
                <div className="cfg-group" key={g.label}>
                  <div className="cfg-group__label">{g.label}</div>
                  <div className="cfg-group__rows">
                    {g.ids.map((id) => {
                      const a = ASSESSMENT_BY_ID[id];
                      if (!a) return null;
                      return <CfgToggleRow key={id} a={a} on={!!active[id]} onClick={() => toggle(id)} />;
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CfgZone>
        </div>
      </aside>
    </>
  );
}
